import React, { useCallback, useEffect, useRef, useState } from "react";

import { renderAsync } from "docx-preview";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/utils/ui";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { MARKER_EXCLUDE_CLASS } from "@constants/marker";

import { ZOOM_DEFAULT, ZOOM_MAX, ZOOM_MIN, ZOOM_STEP } from "./constants";

type DocxPreviewProps = {
  fileUrl: string;
  className?: string;
  /** When true, auto-scale the rendered pages to fit the container width on
   *  initial render. Useful for narrow side-panels where 100% zoom would
   *  clip the right edge of the page. The user can still override via the
   *  zoom controls. */
  fitToWidth?: boolean;
};

/**
 * Read-only DOCX viewer powered by docx-preview (Apache-2.0). Renders the
 * document as paginated HTML — one `<section class="docx">` element per page —
 * which we then drive with our own zoom and page-navigation controls.
 *
 * Replaces a previous Syncfusion-based implementation; Syncfusion is a paid
 * commercial product with no production-grade demo URL, so we moved off it as
 * part of STP-2.
 */
export const DocxPreview: React.FC<DocxPreviewProps> = ({
  fileUrl,
  className,
  fitToWidth = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollSurfaceRef = useRef<HTMLDivElement>(null);
  const hasFitRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(ZOOM_DEFAULT);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState("1");

  // ── Render the docx into the container whenever fileUrl changes.
  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!fileUrl || !container) return;

    setIsLoading(true);
    setError(null);
    setNumPages(0);
    setCurrentPage(1);
    setPageInputValue("1");
    hasFitRef.current = false;
    container.innerHTML = "";

    fetch(fileUrl)
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
        return response.blob();
      })
      .then(async (blob) => {
        if (cancelled) return;
        await renderAsync(blob, container, undefined, {
          inWrapper: true,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          ignoreFonts: false,
          experimental: false,
        });
        if (cancelled) return;
        const pages = container.querySelectorAll<HTMLElement>("section.docx");
        setNumPages(pages.length);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load document");
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  // ── Fit the document width to the available container on first render.
  //    Uses the first rendered .docx section's offsetWidth (intrinsic, before
  //    the CSS transform scale) and the scroll surface's clientWidth.
  useEffect(() => {
    if (!fitToWidth || hasFitRef.current || numPages === 0) return;
    const container = containerRef.current;
    const surface = scrollSurfaceRef.current;
    if (!container || !surface) return;
    const firstPage = container.querySelector<HTMLElement>("section.docx");
    if (!firstPage) return;

    const pageWidth = firstPage.offsetWidth;
    // Reserve a bit of room for the scrollbar so the right edge isn't hidden.
    const availableWidth = surface.clientWidth - 16;
    if (pageWidth > 0 && availableWidth > 0) {
      const fit = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, availableWidth / pageWidth));
      setZoomLevel(fit);
    }
    hasFitRef.current = true;
  }, [fitToWidth, numPages]);

  // ── Track which page is currently in view as the user scrolls.
  useEffect(() => {
    if (numPages === 0) return;
    const container = containerRef.current;
    if (!container) return;
    const pages = Array.from(
      container.querySelectorAll<HTMLElement>("section.docx")
    );
    if (pages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the most-visible page; ties broken by the lowest page index.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              b.intersectionRatio - a.intersectionRatio ||
              pages.indexOf(a.target as HTMLElement) -
                pages.indexOf(b.target as HTMLElement)
          )[0];
        if (!visible) return;
        const idx = pages.indexOf(visible.target as HTMLElement);
        if (idx >= 0) {
          const page = idx + 1;
          setCurrentPage(page);
          setPageInputValue(page.toString());
        }
      },
      { root: container, threshold: [0.25, 0.5, 0.75] }
    );

    pages.forEach((p) => observer.observe(p));
    return () => observer.disconnect();
  }, [numPages]);

  const scrollToPage = useCallback((page: number) => {
    const container = containerRef.current;
    if (!container) return;
    const pages = container.querySelectorAll<HTMLElement>("section.docx");
    const target = pages[page - 1];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const handleZoomIn = useCallback(() => {
    setZoomLevel((z) => Math.min(z + ZOOM_STEP, ZOOM_MAX));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((z) => Math.max(z - ZOOM_STEP, ZOOM_MIN));
  }, []);

  const handlePreviousPage = () => {
    const prev = currentPage - 1;
    if (prev < 1) return;
    setCurrentPage(prev);
    setPageInputValue(prev.toString());
    scrollToPage(prev);
  };

  const handleNextPage = () => {
    const next = currentPage + 1;
    if (next > numPages) return;
    setCurrentPage(next);
    setPageInputValue(next.toString());
    scrollToPage(next);
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPageInputValue(value);
    const page = Math.min(Math.max(parseInt(value) || 1, 1), numPages);
    if (value && !isNaN(page)) {
      setCurrentPage(page);
      scrollToPage(page);
    }
  };

  if (error) {
    return (
      <div className="flex h-full flex-1 flex-col items-center justify-center gap-2">
        <p className="font-medium text-destructive">Failed to load document</p>
        <p className="text-sm text-foreground-muted">{error}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative grid h-full grid-rows-[1fr_auto] overflow-hidden",
        MARKER_EXCLUDE_CLASS
      )}
    >
      <div
        className={cn(
          "relative h-full min-h-0 w-full min-w-0 overflow-hidden rounded-lg border bg-background shadow-sm",
          className
        )}
      >
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background">
            <LoadingIcon className="size-8 animate-spin-ccw" />
            <p className="text-sm text-foreground-muted">
              Loading document preview...
            </p>
          </div>
        )}

        <div className="absolute right-0 top-0 z-20 flex items-center gap-2 p-1">
          <Button
            title={`Zoom out (${Math.round(zoomLevel * 100)}%)`}
            className="rounded-full p-2"
            onClick={handleZoomOut}
            disabled={zoomLevel <= ZOOM_MIN}
          >
            <ZoomOut className="size-4" />
          </Button>
          <Button
            title={`Zoom in (${Math.round(zoomLevel * 100)}%)`}
            className="rounded-full p-2"
            onClick={handleZoomIn}
            disabled={zoomLevel >= ZOOM_MAX}
          >
            <ZoomIn className="size-4" />
          </Button>
        </div>

        {/* Scroll surface for the rendered document. zoom is applied via CSS
            transform on a wrapper inside the container so user scroll position
            is preserved across zoom changes. */}
        <div ref={scrollSurfaceRef} className="h-full w-full overflow-auto">
          <div
            ref={containerRef}
            data-testid="docx-preview-container"
            style={{
              transform: `scale(${zoomLevel})`,
              transformOrigin: "top left",
              width: zoomLevel === 1 ? "100%" : `${100 / zoomLevel}%`,
            }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between p-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground-muted">Page</span>
          <Input
            type="number"
            value={pageInputValue}
            min={1}
            max={numPages || 1}
            onChange={handlePageInputChange}
            onBlur={() => {
              if (!pageInputValue) setPageInputValue(currentPage.toString());
            }}
            className="h-8 w-16 px-2 py-1"
          />
          <span className="text-sm text-foreground-muted">of {numPages || "—"}</span>
        </div>

        <div className="flex gap-2">
          <Button
            variant="simple-outline"
            size="sm"
            onClick={handlePreviousPage}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="size-4" />
            Previous
          </Button>
          <Button
            variant="simple-outline"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage >= numPages || numPages === 0}
          >
            <ChevronRight className="size-4" />
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
